import React from 'react'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/modules/components/ui/carousel'

import Image from 'next/image'
import { TPopProgram } from '~shared/api/programs-popular/types'
import { Hit } from '~shared/icons/hit'
import { useBreakpoint } from '~shared/hooks/useBreakpoint'
import Link from 'next/link'
import { calculateRuCase } from '~shared/utils/calculateRuCase'
import { programLink } from '~shared/utils/program-link'

type Props = {
  popularPrograms: TPopProgram[] | null
}
type Breakpoint = 'xs' | 'md' | 'lg' | 'xl'

export const CarouselDrawer: React.FC<Props> = ({ popularPrograms }) => {
  const breakpoint = useBreakpoint()
  const columnsMap: Record<Breakpoint, number> = {
    xs: 2,
    md: 4,
    lg: 4,
    xl: 3
  }

  const columns = columnsMap[breakpoint] ?? 2

  const slicedPrograms = popularPrograms
    ? Array.from(
        { length: Math.ceil(popularPrograms.length / columns) },
        (_, i) => popularPrograms.slice(i * columns, i * columns + columns)
      )
    : []

  return (
    <Carousel className='w-full'>
      <div className='flex justify-between items-center mb-[24px]'>
        <p className='text-[20px] font-medium'>Популярное</p>
        <div className='flex gap-[8px]'>
          <CarouselPrevious size='sm' />
          <CarouselNext size='sm' />
        </div>
      </div>
      <CarouselContent>
        {slicedPrograms?.map((el, index) => (
          <CarouselItem
            key={`key-${index * 10}`}
            className='flex-col gap-[16px] flex'>
            {el.map(item => (
              <Link
                href={programLink(item)}
                prefetch={false}
                key={item.slug}>
                <div className='flex gap-[16px]'>
                  <Image
                    src={item.image.url}
                    width={80}
                    height={80}
                    className='rounded-[24px] min-w-[80px] max-w-[80px]  min-h-[80px] max-h-[80px] object-cover'
                    alt={item.name ?? ''}
                  />
                  <div className='flex flex-col gap-[8px] justify-center'>
                    <div className='text-[10px] flex gap-[4px]'>
                      <span className='flex items-center rounded-[14px] gap-[4px] bg-[#FFF741] px-[10px] min-h-[22px]'>
                        <Hit size={8} />
                        хит
                      </span>
                      <span
                        className='rounded-[14px] text-[#74A730] flex items-center px-[10px] py-[8px]'
                        style={
                          item.faculty?.color
                            ? {
                                backgroundColor: `${item.faculty?.color}30`,
                                color: item.faculty?.color
                              }
                            : {}
                        }>
                        {item?.faculty?.name}
                      </span>
                      <span className='bg-[#F2F2F2] flex items-center rounded-[16px] px-[10px]'>
                        {item.category?.name}
                      </span>
                    </div>
                    <p className='font-medium line-clamp-2 pb-0.5'>
                      {item.name}
                    </p>
                    <p className='text-[12px] text-[#6A6A6A] mt-[-2px]'>
                      {Number(item.cost?.studyDurationYears) > 0 && (
                        <span>{item.cost?.studyDurationYears} {calculateRuCase('год',item.cost?.studyDurationYears??0,'ru')}</span>
                      )}
                      {Number(item.cost?.studyDurationYears) > 0 &&
                        Number(item.cost?.studyDurationMonths) > 0 && (
                          <span>/ </span>
                        )}
                      {Number(item.cost?.studyDurationMonths) > 0 && (
                        <span>{item.cost?.studyDurationMonths} мес </span>
                      )}
                      {Number(item.cost?.studyHours) > 0 && (
                        <span>/ {item.cost?.studyHours} часов </span>
                      )}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}
